import {
  Radio,
  RadioGroup,
  RadioGroupProps,
  RadioOverrides,
  RadioProps,
} from 'baseui/radio';
import * as React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import merge from 'ts-deepmerge';

import CustomFormControl from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';

export interface RadioGroupInputProps
  extends Omit<RadioGroupProps, 'name'>,
    BaseElementInputProps {
  type: 'radio-group';
  label?: string;
  options: RadioProps[];
  noMargin?: boolean;
  onAfterChange?: (value: string) => void;
  radioOverrides?: RadioOverrides;
}

export default function RadioGroupInput(props: RadioGroupInputProps) {
  const { control } = useFormContext<any>();
  const context = React.useContext(FormContext);
  const { field, fieldState } = useController({ control, name: props.name });

  const {
    readOnly,
    label = '',
    type,
    disabled,
    onChange,
    noMargin,
    options,
    onAfterChange,
    radioOverrides,
    ...rest
  } = props;

  const _isDisabled = !context.editable || readOnly || disabled;

  const overrides = merge(
    {
      RadioMarkOuter: {
        style: ({ $theme, $checked, $disabled }) => ({
          backgroundColor: '#FFFFFF',
          border: `1.6px solid ${
            $disabled
              ? $theme.colors.skyLight
              : $checked
              ? $theme.colors.primaryDark
              : $theme.colors.inkLightest
          }`,
        }),
      },
      RadioMarkInner: {
        style: ({ $theme, $checked, $disabled }) => ({
          width: '12px',
          height: '12px',
          backgroundColor: $checked
            ? $disabled
              ? $theme.colors.skyLight
              : $theme.colors.primaryDark
            : null,
        }),
      },
    } as RadioOverrides,
    { ...radioOverrides },
  );
  const { onChange: fieldOnChange, ...restField } = field;
  const shouldShowError = !!fieldState.error?.message;

  return (
    <CustomFormControl
      label={label}
      noMargin={noMargin}
      error={fieldState.error?.message}
      required={props.required}
      disabled={_isDisabled}
    >
      <RadioGroup
        {...rest}
        {...restField}
        ref={null}
        error={shouldShowError}
        disabled={_isDisabled}
        labelPlacement="right"
        overrides={props.overrides}
        onChange={(e) => {
          if (onChange) {
            onChange(e);
          } else {
            fieldOnChange(e.currentTarget.value);
          }
          onAfterChange && onAfterChange(e.currentTarget.value);
        }}
      >
        {options.map((option) => (
          <Radio key={option.value} {...option} overrides={overrides} />
        ))}
      </RadioGroup>
    </CustomFormControl>
  );
}
